import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';

export type StepButtonSize = 'small' | 'medium';

/** Diameter per size - `small` fits inside a card footer, `medium` is the cart default. */
const SIZE_PX: Record<StepButtonSize, number> = { small: 44, medium: 56 };
const FONT_PX: Record<StepButtonSize, number> = { small: 22, medium: 26 };

/**
 * The glyph is centered with a line-height equal to the button's own size -
 * flex centering alone leaves "+" and "−" a couple of pixels off because of
 * the font's glyph box. Size is a class so the styled component is shared.
 */
const Round = styled.button`
  appearance: none;
  border: none;
  flex: 0 0 auto;
  padding: 0;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-weight: 700;
  text-align: center;
  background: var(--colorBlueGray100);
  color: var(--colorBlueGray1200);
  transition: transform 0.15s ease-out, opacity 0.15s ease-out;

  &.size-medium {
    width: ${SIZE_PX.medium}px;
    height: ${SIZE_PX.medium}px;
    font-size: ${FONT_PX.medium}px;
    line-height: ${SIZE_PX.medium}px;
  }

  &.size-small {
    width: ${SIZE_PX.small}px;
    height: ${SIZE_PX.small}px;
    font-size: ${FONT_PX.small}px;
    line-height: ${SIZE_PX.small}px;
  }

  &.is-touching {
    transform: scale(0.92);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

interface Props {
  /** What the button does - "+" or "−". */
  glyph: '+' | '−';
  onClick: () => void;
  disabled?: boolean;
  size?: StepButtonSize;
  testId?: string;
}

/** One round plus or minus button. Every "+"/"−" in EPOS goes through this. */
const StepButton: FC<Props> = ({ glyph, onClick, disabled = false, size = 'medium', testId }) => {
  const { touching, disabled: d, disabledToast, ...button } = useButton({ onClick, disabled });

  const className = [`size-${size}`, touching && 'is-touching'].filter(Boolean).join(' ');

  return (
    <Round
      {...button}
      disabled={disabled}
      className={className}
      data-testid={testId}
      aria-label={glyph === '+' ? 'increase' : 'decrease'}>
      {glyph}
    </Round>
  );
};

export default memo(StepButton);
